import FaceDeviceLog, { type DeviceLogEntry } from 'components/FaceDeviceLog';
import ProgressDialog from 'components/ProgressDialog';
import { useSyncContext } from 'context/SyncJobContext';
import { getSyncOperationFromJobType, SYNC_OPERATION_META } from 'pages/RecognitionPages/Recognitions/utils';
import { useEffect } from 'react';
import { useCancelJobAsync } from 'services/Jobs/hooks/useMutation';
import type { IDeviceSyncResult } from 'services/Jobs/types';

const parseDevices = (json?: string | null): DeviceLogEntry[] => {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json) as IDeviceSyncResult[];
    return parsed.map((d) => ({
      device: d.Device,
      type: d.Type,
      status: (d.Status === 'pending' ||
      d.Status === 'processing' ||
      d.Status === 'error' ||
      d.Status === 'already_synced' ||
      d.Status === 'ok'
        ? d.Status
        : // Status desconhecido/ausente é tratado como falha (fail-closed) em vez de sucesso —
          // evita mascarar um estado real não reconhecido como se tivesse dado certo.
          'error') as DeviceLogEntry['status'],
      error: d.Error ?? undefined,
    }));
  } catch {
    return [];
  }
};

const FaceSyncRunner = () => {
  const syncCtx = useSyncContext();
  const jobId = syncCtx.job?.jobId ?? null;
  const isPreparing = !jobId;

  // Poll do job (useJobQuery) e as decisões de conclusão (successFlash, snackbar de 0 dispositivo)
  // vivem no SyncJobProvider (topo do app), não aqui — esse componente só existe montado dentro da
  // página de Faces, e o job precisa continuar sendo acompanhado mesmo se o usuário sair dela (B17).
  const jobData = syncCtx.jobData;
  const { mutate: cancelJob } = useCancelJobAsync();

  useEffect(() => {
    syncCtx.registerViewCallback(() => syncCtx.hideToast());
    syncCtx.registerCloseCallback(() => syncCtx.clearSyncJob());
    return () => {
      syncCtx.registerViewCallback(null);
      syncCtx.registerCloseCallback(null);
    };
  }, [
    syncCtx.registerViewCallback,
    syncCtx.registerCloseCallback,
    syncCtx.hideToast,
    syncCtx.clearSyncJob,
  ]);

  if (isPreparing && !syncCtx.preparingName) return null;
  if (!isPreparing && !syncCtx.job) return null;

  const devices = jobData ? parseDevices(jobData.logData ?? jobData.resultData) : [];

  // Job concluído com 0 dispositivo: o SyncJobProvider já disparou o snackbar e limpou o job.
  // Fecha o dialog aqui, um único unmount final, não uma piscada no meio do fluxo.
  if (jobData?.status === 'Completed' && devices.length === 0) return null;

  const operation = isPreparing
    ? syncCtx.preparingOperation ?? 'create'
    : syncCtx.job?.operation ?? getSyncOperationFromJobType(jobData?.jobType);
  const meta = SYNC_OPERATION_META[operation];

  const hasErrors = devices.some((d) => d.status === 'error');
  const status: 'loading' | 'success' | 'partial' | 'error' | 'canceled' = !jobData
    ? 'loading'
    : jobData.status === 'Completed'
      ? hasErrors
        ? 'partial'
        : 'success'
      : jobData.status === 'Failed'
        ? 'error'
        : jobData.status === 'Canceled'
          ? 'canceled'
          : 'loading';

  const displayName = isPreparing ? syncCtx.preparingName : syncCtx.job?.personName;

  return (
    <ProgressDialog
      open={isPreparing || !syncCtx.isToastVisible}
      onClose={() => (isPreparing ? syncCtx.stopPreparing() : syncCtx.clearSyncJob())}
      onCancel={jobId ? () => cancelJob(jobId) : undefined}
      onMinimize={jobId ? () => syncCtx.showToast() : undefined}
      progress={jobData?.progress ?? 5}
      status={status}
      statusMessage={jobData?.message ?? meta.preparingMessage}
      title={`${meta.label}: ${displayName ?? ''}`}
      icon={meta.icon}
      accentColor={meta.color}
      maxWidth="sm"
      segmentDevices={
        devices.length > 0
          ? devices.map((d) => ({ name: d.device, status: d.status }))
          : undefined
      }
    >
      {devices.length > 0 && <FaceDeviceLog devices={devices} />}
    </ProgressDialog>
  );
};

export default FaceSyncRunner;
